import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../api';
import { ErrorState, Loading } from '../ui/State';
import { PageHeader } from '../ui/PageHeader';
type Payment={id:string;reference:string;account_reference:string;amount:string;status:string};
type Receipt={id:string;posting_date:string;external_reference:string|null;credit_amount:string};
export function CardReconciliationAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building') ?? '',
    root = '/api/alpha/card-payments/' + building;
  const [payments, setPayments] = useState<Payment[]>(),
    [error, setError] = useState<unknown>();
  const load = () => api<{ payments: Payment[] }>(root).then((r) => setPayments(r.payments));
  useEffect(() => {
    const controller=new AbortController();setPayments(undefined);setError(undefined);
    if(building)void api<{payments:Payment[]}>(root,{signal:controller.signal}).then(r=>{
      if(!controller.signal.aborted)setPayments(r.payments);
    }).catch(cause=>{if(!controller.signal.aborted)setError(cause)});
    return()=>controller.abort();
  }, [building]);
  return (
    <>
      <PageHeader title="Reconcile card payments"
        context="Check the provider, then link the existing bank receipt allocated to the demand. Create and allocate any missing receipt in Income first. No additional account credit is created here."
        secondaryActions={building?[{kind:'link',label:'Open income control',to:'/finance/service-charges?building='+building}]:[]}/>
      {Boolean(error) && <ErrorState error={error} />}
      {!building?<p>Choose a building to review its card payments.</p>:!payments&&!error?<Loading/>:null}
      {payments?.length===0&&<p className="empty">No card payments were returned for this building.</p>}
      {payments?.map((p) => (
        <Card key={p.id} payment={p} root={root} reload={load} />
      ))}
    </>
  );
}
function Card({
  payment: p,
  root,
  reload,
}: {
  payment: Payment;
  root: string;
  reload: () => Promise<unknown>;
}) {
  const [receipts, setReceipts] = useState<Receipt[]>([]),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void api<{ receipts: Receipt[] }>(root + '/' + p.id + '/receipts')
      .then((r) => setReceipts(r.receipts))
      .catch(setError);
  }, [p.id, p.status]);
  async function save(action: string, body: object = {}) {
    setBusy(true);
    try {
      await api(root + '/' + p.id + '/' + action, {
        method: 'POST',
        body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
      });
      await reload();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>
        {p.reference} · {p.account_reference}
      </h2>
      <p>
        £{p.amount} · {p.status}
      </p>
      {Boolean(error) && <ErrorState error={error} />}
      <button disabled={busy} onClick={() => void save('refresh')}>
        Check provider
      </button>
      {p.status === 'Paid' && (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void save('reconcile', { receiptPostingId: f.get('receipt'), reason: f.get('reason') });
          }}
        >
          <label>
            Bank receipt
            <select name="receipt" required>
              <option value="">Choose the matching receipt</option>
              {receipts.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.posting_date} · {r.external_reference || 'Receipt'} · £{r.credit_amount}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reconciliation evidence
            <textarea name="reason" required minLength={10} maxLength={2000} />
          </label>
          <button disabled={busy}>Confirm reconciliation</button>
        </form>
      )}
    </section>
  );
}
